import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { EventCard as EventCardData, EventDetail } from '@/types'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import {
    ArrowLeft,
    CalendarDays,
    CalendarPlus,
    Check,
    Clock,
    Copy,
    ExternalLink,
    Globe,
    Lock,
    MapPin,
    Ticket
} from 'lucide-react'
import { getSession } from '@/lib/auth'
import { getRequestOrigin } from '@/lib/api'
import { isStripeConfigured } from '@/lib/stripe'
import { cancelCheckout } from '@/lib/orders'
import { getEventBySlug, listPublicEvents } from '@/lib/events'
import { calculateServiceFee, cms, formatMoney, formatPrice, initials } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { EventCard } from '@/components/events/event-card'
import { CategoryIcon } from '@/components/events/category-icon'
import { CheckoutDialog } from '@/components/events/checkout-dialog'
import { TicketSelector, formatTotal, summarizeSelection } from '@/components/events/ticket-selector'

interface EventPageProps {
    event: EventDetail
    moreEvents: Array<EventCardData>
    paymentsReady: boolean
    isOwnerPreview: boolean
    checkoutCancelled: boolean
}

const EventPage: FC<EventPageProps> = ({ event, moreEvents, paymentsReady, isOwnerPreview, checkoutCancelled }): ReactNode => {
    const [quantities, setQuantities] = useState<Record<string, number>>({})
    const [checkoutOpen, setCheckoutOpen] = useState(false)
    const [copied, setCopied] = useState(false)

    const { count, paidCount, subtotal } = summarizeSelection(event.ticketTypes, quantities)
    const estimatedFee = calculateServiceFee(subtotal, paidCount)
    const salesOpen = event.status === 'published' && !event.hasEnded
    const anyAvailable = event.ticketTypes.some(type => type.available)
    const location = [event.address, event.city].filter(Boolean).join(', ')
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([event.venueName, event.address, event.city].filter(Boolean).join(', '))}`

    useEffect(() => {
        setQuantities({})
    }, [event.id])

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href.split('?')[0])
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
        catch {
            setCopied(false)
        }
    }

    const priceFrom = event.minPrice === null ? null : event.minPrice === 0 ? 'Free' : `From ${formatPrice(event.minPrice, event.currency)}`

    return (
        <Layout>
            <Seo
                title={event.title}
                description={event.summary ?? `Get tickets for ${event.title} on ${event.dateLabel}.`}
                ogImage={event.coverImage ?? undefined}
                noIndex={event.status !== 'published'}
            />

            {isOwnerPreview && (
                <div className='border-b border-warning/30 bg-warning-soft'>
                    <div className='mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm sm:px-6 lg:px-8'>
                        <span className='font-medium'>
                            You are previewing a {event.status === 'draft' ? 'draft' : event.status} event. Only you can see this page.
                        </span>
                        <Link href={`/dashboard/events/${event.id}`} className='font-semibold text-primary hover:underline'>
                            Manage event
                        </Link>
                    </div>
                </div>
            )}

            <article className='pb-28 lg:pb-20'>
                <section id='event-header' className='mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8'>
                    <Link href='/events' className='mb-5 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground'>
                        <ArrowLeft className='size-4' aria-hidden='true' />
                        All events
                    </Link>

                    <div className='relative aspect-[16/9] overflow-hidden rounded-[2rem] bg-muted sm:aspect-[21/9]'>
                        {event.coverImage
                            ? <img src={event.coverImage} alt={event.coverImageAlt ?? ''} className='size-full object-cover' />
                            : (
                                <div className='flex size-full items-center justify-center bg-gradient-to-br from-primary to-ink'>
                                    <CategoryIcon slug={event.category?.slug} className='size-16 text-white/60' />
                                </div>
                            )}
                    </div>
                </section>

                <div className='mx-auto grid max-w-7xl gap-10 px-4 pt-10 sm:px-6 lg:grid-cols-[1fr_400px] lg:gap-14 lg:px-8'>
                    <div className='flex min-w-0 flex-col gap-10'>
                        <div className='flex flex-col gap-5'>
                            <div className='flex flex-wrap items-center gap-2'>
                                {event.category && (
                                    <Link href={`/events?category=${event.category.slug}`}>
                                        <Badge variant='primary' className='px-3 py-1'>
                                            <CategoryIcon slug={event.category.slug} />
                                            <span data-mdlfy-cms={cms('categories', event.category.id, 'name')}>{event.category.name}</span>
                                        </Badge>
                                    </Link>
                                )}

                                {event.status === 'cancelled' && <Badge variant='danger' className='px-3 py-1'>Cancelled</Badge>}
                                {event.hasEnded && <Badge className='px-3 py-1'>Event ended</Badge>}
                            </div>

                            <h1 className='text-4xl font-extrabold leading-[1.05] sm:text-5xl' data-mdlfy-cms={cms('events', event.id, 'title')}>
                                {event.title}
                            </h1>

                            {event.summary && (
                                <p className='text-lg leading-relaxed text-muted-foreground' data-mdlfy-cms={cms('events', event.id, 'summary')}>
                                    {event.summary}
                                </p>
                            )}
                        </div>

                        {checkoutCancelled && (
                            <Alert variant='info' title='Payment cancelled'>
                                No payment was taken and your tickets were released. You can pick tickets again whenever you are ready.
                            </Alert>
                        )}

                        {event.status === 'cancelled' && (
                            <Alert variant='error' title='This event has been cancelled'>
                                The organizer has cancelled this event. If you bought tickets, the organizer will be in touch about refunds.
                            </Alert>
                        )}

                        <section id='details' aria-label='Date and location' className='grid gap-4 sm:grid-cols-2'>
                            <div className='flex gap-4 rounded-3xl border border-border bg-card p-5'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary'>
                                    <CalendarDays className='size-5' aria-hidden='true' />
                                </span>

                                <div className='flex flex-col gap-1'>
                                    <span className='font-semibold'>{event.dateLabel}</span>
                                    <span className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                                        <Clock className='size-3.5' aria-hidden='true' />
                                        {event.startTimeLabel} to {event.endTimeLabel} {event.zoneLabel}
                                    </span>
                                    <a href={`/api/calendar/${event.slug}`} className='mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'>
                                        <CalendarPlus className='size-4' aria-hidden='true' />
                                        Add to calendar
                                    </a>
                                </div>
                            </div>

                            <div className='flex gap-4 rounded-3xl border border-border bg-card p-5'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary'>
                                    {event.isOnline ? <Globe className='size-5' aria-hidden='true' /> : <MapPin className='size-5' aria-hidden='true' />}
                                </span>

                                <div className='flex min-w-0 flex-col gap-1'>
                                    {event.isOnline
                                        ? (
                                            <>
                                                <span className='font-semibold'>Online event</span>
                                                <span className='text-sm text-muted-foreground'>The joining link is shared on your ticket page.</span>
                                            </>
                                        )
                                        : (
                                            <>
                                                {event.venueName && <span className='font-semibold' data-mdlfy-cms={cms('events', event.id, 'venue_name')}>{event.venueName}</span>}
                                                {location && (
                                                    <span className='text-sm text-muted-foreground'>
                                                        {event.address && <span data-mdlfy-cms={cms('events', event.id, 'address')}>{event.address}</span>}
                                                        {event.address && event.city && <span>, </span>}
                                                        {event.city && <span data-mdlfy-cms={cms('events', event.id, 'city')}>{event.city}</span>}
                                                    </span>
                                                )}
                                                <a href={mapsUrl} target='_blank' rel='noopener noreferrer' className='mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'>
                                                    <ExternalLink className='size-4' aria-hidden='true' />
                                                    Open in maps
                                                </a>
                                            </>
                                        )}
                                </div>
                            </div>
                        </section>

                        {event.description && (
                            <section id='about' className='flex flex-col gap-4'>
                                <h2 className='text-2xl font-bold'>About this event</h2>
                                <div className='whitespace-pre-line text-base leading-relaxed text-foreground/85' data-mdlfy-cms={cms('events', event.id, 'description')}>
                                    {event.description}
                                </div>
                            </section>
                        )}

                        <section id='organizer' className='flex flex-col gap-4'>
                            <h2 className='text-2xl font-bold'>Hosted by</h2>

                            <Link
                                href={`/o/${event.organizer.slug}`}
                                className='flex items-center gap-4 rounded-3xl border border-border bg-card p-5 transition hover:border-primary/40 hover:shadow-md'
                            >
                                {event.organizerLogo
                                    ? <img src={event.organizerLogo} alt={event.organizerLogoAlt ?? ''} className='size-14 shrink-0 rounded-2xl object-cover' />
                                    : (
                                        <span className='flex size-14 shrink-0 items-center justify-center rounded-2xl bg-ink font-heading text-lg font-bold text-ink-foreground' aria-hidden='true'>
                                            {initials(event.organizer.name)}
                                        </span>
                                    )}

                                <span className='flex min-w-0 flex-col gap-0.5'>
                                    <span className='font-semibold' data-mdlfy-cms={cms('organizers', event.organizer.id, 'name')}>{event.organizer.name}</span>
                                    {event.organizerTagline && (
                                        <span className='text-sm text-muted-foreground' data-mdlfy-cms={cms('organizers', event.organizer.id, 'tagline')}>
                                            {event.organizerTagline}
                                        </span>
                                    )}
                                </span>
                            </Link>
                        </section>
                    </div>

                    <aside id='tickets' aria-label='Tickets' className='lg:sticky lg:top-24 lg:self-start'>
                        <div className='flex flex-col gap-5 rounded-[2rem] border border-border bg-card p-6 shadow-xl shadow-ink/5'>
                            <div className='flex items-center justify-between gap-4'>
                                <h2 className='flex items-center gap-2 text-xl font-bold'>
                                    <Ticket className='size-5 text-primary' aria-hidden='true' />
                                    Tickets
                                </h2>

                                <button
                                    type='button'
                                    onClick={copyLink}
                                    className='inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                                >
                                    {copied ? <Check className='size-4 text-success' aria-hidden='true' /> : <Copy className='size-4' aria-hidden='true' />}
                                    {copied ? 'Link copied' : 'Copy link'}
                                </button>
                            </div>

                            {salesOpen
                                ? (
                                    <>
                                        <TicketSelector
                                            ticketTypes={event.ticketTypes}
                                            currency={event.currency}
                                            quantities={quantities}
                                            onChange={setQuantities}
                                        />

                                        <div className='flex flex-col gap-3 border-t border-dashed border-border pt-5'>
                                            <div className='flex items-baseline justify-between gap-4'>
                                                <span className='text-sm text-muted-foreground'>
                                                    {count === 0 ? 'Select tickets above' : `${count} ${count === 1 ? 'ticket' : 'tickets'}`}
                                                </span>
                                                <span className='font-heading text-2xl font-bold tabular-nums'>
                                                    {count === 0 ? priceFrom ?? '' : formatTotal(subtotal, event.currency)}
                                                </span>
                                            </div>

                                            {estimatedFee > 0 && (
                                                <p className='text-xs text-muted-foreground'>
                                                    Plus {formatMoney(estimatedFee, event.currency)} service fee, shown at checkout.
                                                </p>
                                            )}

                                            <Button size='lg' disabled={count === 0} onClick={() => setCheckoutOpen(true)}>
                                                {anyAvailable ? 'Get tickets' : 'Sold out'}
                                            </Button>

                                            <p className='flex items-center justify-center gap-1.5 text-xs text-muted-foreground'>
                                                <Lock className='size-3.5' aria-hidden='true' />
                                                Instant QR tickets by email
                                            </p>
                                        </div>
                                    </>
                                )
                                : (
                                    <p className='rounded-2xl bg-muted p-5 text-sm text-muted-foreground'>
                                        {event.status === 'cancelled'
                                            ? 'Ticket sales are closed because this event was cancelled.'
                                            : event.hasEnded
                                                ? 'This event has ended, so tickets are no longer on sale.'
                                                : 'Tickets go on sale once the organizer publishes this event.'}
                                    </p>
                                )}
                        </div>
                    </aside>
                </div>

                {moreEvents.length > 0 && (
                    <section id='more-events' className='mx-auto mt-20 max-w-7xl px-4 sm:px-6 lg:px-8'>
                        <div className='mb-8 flex items-end justify-between gap-4'>
                            <h2 className='text-2xl font-bold sm:text-3xl'>
                                More from <span data-mdlfy-cms={cms('organizers', event.organizer.id, 'name')}>{event.organizer.name}</span>
                            </h2>
                            <Link href={`/o/${event.organizer.slug}`} className='text-sm font-semibold text-primary hover:underline'>
                                View all
                            </Link>
                        </div>

                        <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {moreEvents.map(item => <EventCard key={item.id} event={item} />)}
                        </div>
                    </section>
                )}
            </article>

            {salesOpen && (
                <div className='fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 p-4 backdrop-blur lg:hidden'>
                    <div className='mx-auto flex max-w-3xl items-center justify-between gap-4'>
                        <div className='flex flex-col'>
                            <span className='text-xs text-muted-foreground'>{count > 0 ? `${count} selected` : 'Tickets'}</span>
                            <span className='font-heading text-lg font-bold'>
                                {count > 0 ? formatTotal(subtotal, event.currency) : priceFrom ?? 'Coming soon'}
                            </span>
                        </div>

                        {count > 0
                            ? <Button onClick={() => setCheckoutOpen(true)}>Checkout</Button>
                            : <a href='#tickets' className={buttonVariants({ variant: 'primary' })}>{anyAvailable ? 'Choose tickets' : 'Sold out'}</a>}
                    </div>
                </div>
            )}

            <CheckoutDialog
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                eventId={event.id}
                eventTitle={event.title}
                currency={event.currency}
                ticketTypes={event.ticketTypes}
                quantities={quantities}
                paymentsReady={paymentsReady}
            />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<EventPageProps> = async ({ params, query, req }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    try {
        const cancelledToken = typeof query.cancelled === 'string' ? query.cancelled : null
        if (cancelledToken) await cancelCheckout(cancelledToken, getRequestOrigin(req))

        const event = await getEventBySlug(slug)
        if (!event) return { notFound: true }

        let isOwnerPreview = false

        if (event.status === 'draft') {
            const session = await getSession(req)
            if (!session || session.organizer.id !== event.organizer.id) return { notFound: true }
            isOwnerPreview = true
        }

        const more = await listPublicEvents({ organizerId: event.organizer.id, limit: 4 })

        return {
            props: {
                event,
                moreEvents: more.events.filter(item => item.id !== event.id).slice(0, 3),
                paymentsReady: isStripeConfigured(),
                isOwnerPreview,
                checkoutCancelled: Boolean(cancelledToken)
            }
        }
    }
    catch (error) {
        console.error('Event page failed to load:', error instanceof Error ? error.message : error)
        throw error
    }
}

export default EventPage
